<template>
  <v-dialog
    v-model="visible"
    :fullscreen="fullscreen"
    :max-width="fullscreen ? '100%' : maxWidth"
    :persistent="persistent"
  >
    <StandardCard
      @title-action-clicked="close"
      :title="title || undefined"
      :title-action="title ? closeTitleAction : undefined"
      card-text-class="mnh-250"
      border
      tile
    >
      <template
        v-if="!title && !persistent"
        #title
      >
        <div class="d-flex justify-end w-100">
          <StandardButton
            @click="close"
            aria-label="Close"
            color="grey"
            icon="close"
            size="small"
            variant="text"
          />
        </div>
      </template>

      <template v-if="loaded">
        <QuestionSet
          v-model="record"
          @back="back($event)"
          @next="forward($event)"
          :attachment-owner-id="record?.id"
          :attachment-owner-type="attachmentOwnerType"
          :processing="processing"
          :questions="questions"
          :schema="schema?.definition"
          :section="section"
          :transition-name="transitionName"
          key-name="question"
        />
      </template>

      <template v-else-if="empty">
        <div
          class="mnh-250 d-flex flex-column justify-center align-center ga-4 px-4 text-center"
          data-testid="schematized-question-stepper-empty"
        >
          <p class="mb-0 fs-16">
            {{ $bct('No questions configured for this schema.') }}
          </p>
          <StandardButton
            @click="close"
            role="link"
            text="Close"
          />
        </div>
      </template>

      <template v-else>
        <div class="mnh-250 d-flex justify-center align-center">
          <v-progress-linear indeterminate />
        </div>
      </template>
    </StandardCard>
  </v-dialog>
</template>

<script setup>
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

import QuestionSet from '@/shared/components/form/QuestionSet.vue';
import useQuestionSet from '@/shared/composables/useQuestionSet';
import useRouterHelper from '@/shared/composables/useRouterHelper';
import useStepper from '@/shared/composables/useStepper';

defineExpose({ open, close, isOpen });

const props = defineProps({
  attachmentOwnerType: { type: String, default: null },
  fullscreen: { type: Boolean, default: false },
  maxWidth: { type: [Number, String], default: 800 },
  onComplete: { type: Function, default: null },
  onExit: { type: Function, default: null },
  onSave: { type: Function, required: true },
  persistent: { type: Boolean, default: true },
  saveOnBack: { type: Boolean, default: false },
  schemaId: { type: String, required: true },
  showUnpublishedQuestions: { type: Boolean, default: false },
  syncedModel: { type: String, required: true },
  syncRouteQuery: { type: Boolean, default: false },
  title: { type: String, default: null },
});

const $bct = inject('translator');

const emit = defineEmits(['close', 'save']);

const route = useRoute();
const store = useStore();
const { updateQuery: routerUpdateQuery } = useRouterHelper();

const record = ref(null);
const processing = ref(false);
const visible = ref(false);

const schemaIdRef = computed(() => props.schemaId);
const syncedModelRef = computed(() => props.syncedModel);

const schema = computed(() => store.state.schemas?.[props.schemaId]);

const closeTitleAction = computed(() => {
  if (props.persistent) return null;
  return { icon: 'close', variant: 'text', ariaLabel: 'Close' };
});

async function updateQuery(params) {
  if (!props.syncRouteQuery) return;
  await routerUpdateQuery(params);
}

const { questions, questionsLoaded, loadQuestions, cancelPendingRefetch } = useQuestionSet({
  schemaIdRef,
  syncedModelRef,
  recordRef: record,
  includeUnpublished: props.showUnpublishedQuestions,
});

const { section, transitionName, stepBack, stepForward } = useStepper({
  processing,
  route,
  updateQuery,
});

const loaded = computed(
  () => visible.value && record.value && questionsLoaded.value && questions.value.length > 0,
);

const empty = computed(
  () => visible.value && record.value && questionsLoaded.value && questions.value.length === 0,
);

function isOpen() {
  return visible.value === true;
}

async function open(initialRecord = {}) {
  record.value = { ...initialRecord };
  visible.value = true;
  section.value = 'question-0';
  await updateQuery({ section: 'question-0', step: 1 });
  await loadQuestions();
}

async function close() {
  cancelPendingRefetch();
  visible.value = false;
  record.value = null;
  questionsLoaded.value = false;
  await updateQuery({ section: null, step: null });
  emit('close');
}

async function back(index) {
  if (processing.value) return;
  processing.value = true;

  try {
    if (props.saveOnBack && record.value) {
      const response = await props.onSave(record.value, {
        index,
        isLast: false,
        direction: 'back',
      });
      if (response?.data) {
        record.value = { ...record.value, ...response.data };
      }
      await loadQuestions();
    }

    if (index - 1 < 0) {
      processing.value = false;
      if (props.onExit) await props.onExit();
      await close();
      return;
    }

    stepBack(`question-${index - 1}`);
  } finally {
    processing.value = false;
  }
}

async function forward(index) {
  if (processing.value) return;
  processing.value = true;

  try {
    const isLast = index + 1 >= questions.value.length;
    const response = await props.onSave(record.value, { index, isLast, direction: 'forward' });
    if (!response?.data) return;

    record.value = { ...record.value, ...response.data };
    await loadQuestions();

    if (index + 1 >= questions.value.length) {
      emit('save', response.data);
      if (props.onComplete) await props.onComplete(response.data);
      processing.value = false;
      await close();
      return;
    }

    stepForward(`question-${index + 1}`);
  } finally {
    if (processing.value) processing.value = false;
  }
}
</script>
